import { useCallback, useState } from 'react';
import { Check, Copy, Link2, Loader2, RefreshCw, AlertTriangle, ShieldOff } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { documentCaptureService } from '../../services/documentCaptureService';
import { absoluteShareUrl, copyAudienceHint, FAMILIES } from '../../config/families';
import type { ShareLink } from '../../types/documentCapture.types';
import { apiErrorInfo, formatVersionStamp } from '../../lib/versionFormat';

/**
 * Document link management (86d3udn5u T8, reworked by 86d3uu104 T10).
 *
 * T10 changed what this dialog is FOR. Every document now has exactly one capability link — it IS the
 * value in the NetSuite field — so there is nothing to "create", and the old Revoke/Regenerate pair
 * becomes a single atomic **Replace**. What remains: see the link, copy it, replace it if it leaked,
 * and read the access trail.
 *
 * Gated by `docscan.share` for ALL families: rotate and the trail are the privileged parts, and
 * hiding the dialog for non-INV/DR families would leave their links unrotatable. Plain copying needs
 * only the family's view right and happens from the viewer toolbar, straight off the document payload.
 */
interface SharePublicLinkDialogProps {
  docNumber: string;
  prefix: string;
  /** Fencing token — replace echoes it back so a stale view answers 409 instead of acting. */
  linkEpoch: number;
  /** Called after a successful replace so the parent can re-read the document's new link. */
  onRotated?: () => void;
}

type LoadState = 'loading' | 'ready' | 'none' | 'revoked';

export function SharePublicLinkDialog({ docNumber, prefix, linkEpoch, onRotated }: SharePublicLinkDialogProps) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<LoadState>('loading');
  const [link, setLink] = useState<ShareLink | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** 14ykpqtwauu — Replace was refused, so Revoke is offered as the kill that cannot be. */
  const [replaceRefused, setReplaceRefused] = useState(false);
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);

  const shareUrl = absoluteShareUrl(link?.url) ?? '';
  const customerFacing = FAMILIES[prefix]?.shareable ?? false;

  const load = useCallback(async () => {
    setState('loading');
    setError(null);
    try {
      const existing = await documentCaptureService.getShareLink(docNumber);
      setLink(existing);
      setState(existing ? 'ready' : 'none');
    } catch (err) {
      setError(apiErrorInfo(err).message ?? 'Couldn’t load this document’s link.');
      setState('none');
    }
  }, [docNumber]);

  const onOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      setCopied(false);
      setConfirming(false);
      setReplaceRefused(false);
      setConfirmingRevoke(false);
      setError(null);
      if (next) void load();
    },
    [load]
  );

  const copy = useCallback(async () => {
    if (!shareUrl) return;
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success('Link copied', { description: copyAudienceHint(prefix) });
    setTimeout(() => setCopied(false), 1500);
  }, [shareUrl, prefix]);

  /**
   * Replace is the FIRST kill here, deliberately (owner decision, 86d3uu104): the NetSuite field must
   * always hold a link that works. 14ykpqtwauu adds Revoke ONLY as the fallback when Replace is refused —
   * see `revoke` below.
   *
   * A revoke-without-replace was considered and rejected. It leaves the field pointing at a dead
   * token, and the nightly sweep — which exists to keep that field valid — would notice a linked
   * document with no live token and mint a fresh one anyway. So the button would have promised a kill
   * it could not keep, and would have re-published a working public link a day later, unattended.
   * Replacing the token achieves the same containment (the leaked URL dies immediately) while keeping
   * the invariant intact.
   */
  const rotate = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const rotated = await documentCaptureService.rotateShareLink(docNumber, linkEpoch);
      setLink(rotated);
      setState(rotated ? 'ready' : 'none');
      setConfirming(false);
      toast.success('Link replaced', {
        description: 'NetSuite now points at the new link. Copies already sent no longer work.',
      });
      onRotated?.();
    } catch (err) {
      const { status, message } = apiErrorInfo(err);
      if (status === 409) {
        // `linkEpoch` is a prop, and only a SUCCESSFUL rotate refreshed it — so closing and reopening
        // re-sent the same stale epoch and 409'd forever, with the old copy telling the user to do
        // exactly that. Ask the parent to re-read the document so the next attempt carries a live
        // epoch (the restore action in this feature already does this on 409).
        onRotated?.();
        setError('Someone else changed this document just now. It has been refreshed — try again.');
      } else if (status !== 403) {
        // A NetSuite refusal (closed period) arrives here VERBATIM — and because the whole thing is
        // one transaction, nothing changed and the current link still works. Say both parts.
        setError(message ?? 'The link wasn’t replaced — the current one still works.');
        // 14ykpqtwauu — a refused Replace used to leave a leaked link with NO way to kill it. The
        // applied-credits refusal is gone since 14ykpqtx1z9, but a closed period still refuses, so the
        // local-only revoke stays as the kill for that case.
        if (status === 400) setReplaceRefused(true);
        setConfirming(false);
      }
    } finally {
      setBusy(false);
    }
  }, [docNumber, linkEpoch, onRotated]);

  /**
   * 14ykpqtwauu — the kill for when Replace cannot be done. Local only, so NetSuite cannot refuse it.
   *
   * 86d3uu104 rejected a standalone revoke because the nightly sweep would quietly publish a fresh link,
   * so "revoke" promised a kill it could not keep. That reasoning holds while Replace works, which is
   * why this is offered only after Replace is refused. Here the leaked URL dies immediately and
   * NetSuite keeps the dead link until the sweep re-links it — for a journal, through NetSuite's link
   * service (14ykpqtx1z9), which leaves its credit applications alone.
   */
  const revoke = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await documentCaptureService.revokeShareLink(docNumber, linkEpoch);
      setLink(null);
      setState('revoked');
      setConfirmingRevoke(false);
      setReplaceRefused(false);
      toast.success('Link revoked', { description: 'Copies already sent no longer work.' });
      onRotated?.();
    } catch (err) {
      const { status, message } = apiErrorInfo(err);
      if (status === 409) {
        onRotated?.();
        setError('Someone else changed this document just now. It has been refreshed — try again.');
      } else if (status !== 403) {
        setError(message ?? 'The link wasn’t revoked — try again.');
      }
    } finally {
      setBusy(false);
    }
  }, [docNumber, linkEpoch, onRotated]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 gap-1.5" aria-label="Manage link">
              <Link2 className="h-3.5 w-3.5" aria-hidden="true" />
              Manage link
            </Button>
          </DialogTrigger>
        </TooltipTrigger>
        <TooltipContent className="max-w-64">
          See who has opened this document’s link, and replace it if it has gone somewhere it shouldn’t.
        </TooltipContent>
      </Tooltip>

      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Document link · {docNumber}</DialogTitle>
          <DialogDescription>
            {customerFacing
              ? 'This is the link NetSuite points at. Anyone who opens it can view and download this document without signing in.'
              : 'This is the link NetSuite points at. Only staff with permission for this document type can open it — everyone else sees “unavailable”.'}
          </DialogDescription>
        </DialogHeader>

        {state === 'loading' ? (
          <div className="space-y-3" aria-busy="true">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-16 w-full rounded-lg" />
            <span className="sr-only">Loading link…</span>
          </div>
        ) : state === 'revoked' ? (
          <div className="space-y-2 rounded-lg border border-border bg-muted/40 p-3">
            <p className="text-sm font-medium text-foreground">Link revoked</p>
            <p className="text-xs leading-relaxed text-muted-foreground">
              Copies already sent no longer work. NetSuite still points at the dead link until DOP can
              link this document again — for a journal with applied credits, that comes back as a Needs
              review item rather than a save.
            </p>
          </div>
        ) : state === 'none' ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              This document doesn’t have a link yet — one is created automatically when it links to its
              NetSuite transaction.
            </p>
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>
        ) : (
          link && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Input readOnly value={shareUrl} className="font-mono text-xs" aria-label="Document link" />
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  onClick={() => void copy()}
                  aria-label="Copy link"
                >
                  {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>

              <div className="rounded-lg border border-border bg-muted/40 p-3">
                <p className="text-xs font-medium text-foreground">Access trail</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {link.openCount > 0
                    ? `Opened ${link.openCount} ${link.openCount === 1 ? 'time' : 'times'}${
                        link.lastOpenedAt ? ` · last ${formatVersionStamp(link.lastOpenedAt)}` : ''
                      }`
                    : 'Not opened yet'}
                </p>
                {/* Say what this number actually measures. It counts opens of the CUSTOMER view only
                    — a staffer following this link lands on the internal viewer instead and is not
                    counted here. Without the note the figure reads as "nobody has seen this
                    document", and for a staff-only family it can never move at all, so a bare
                    "Not opened yet" is false reassurance (86d3uu104).

                    Deliberately says "not shown here" rather than naming a place to look: staff views
                    ARE stamped (every download writes a read-audit row, for every family) but that
                    record has no screen and no endpoint — it is reachable only by query. Pointing a
                    user at a destination that doesn't exist is worse than admitting there isn't one. */}
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                  {customerFacing ? (
                    <>
                      Counts <span className="font-medium text-foreground">customer</span> opens of
                      this link. Staff who follow it go straight to the internal viewer — their views
                      are recorded in the audit log, not counted here.
                    </>
                  ) : (
                    <>
                      Customers can’t open this document type, so this stays at zero even when the
                      link is used. Staff views are recorded in the audit log, not counted here.
                    </>
                  )}
                </p>
              </div>

              {error && <p className="text-xs text-destructive">{error}</p>}

              {replaceRefused && (
                <div className="space-y-3 rounded-lg border border-destructive/25 bg-destructive/5 p-3">
                  <p className="text-xs leading-relaxed text-foreground">
                    If this link has leaked, revoke it instead. Anyone holding it is locked out right away.
                    NetSuite keeps pointing at the dead link until DOP can link this document again.
                  </p>
                  {confirmingRevoke ? (
                    <div className="flex gap-2">
                      <Button
                        variant="destructive"
                        size="sm"
                        className="flex-1 gap-1.5"
                        onClick={() => void revoke()}
                        disabled={busy}
                      >
                        {busy && (
                          <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                        )}
                        Revoke it
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="flex-1"
                        onClick={() => setConfirmingRevoke(false)}
                        disabled={busy}
                      >
                        Keep it
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full gap-1.5"
                      onClick={() => setConfirmingRevoke(true)}
                      disabled={busy}
                    >
                      <ShieldOff className="h-3.5 w-3.5" aria-hidden="true" />
                      Revoke link
                    </Button>
                  )}
                </div>
              )}

              {replaceRefused ? null : confirming ? (
                <div className="space-y-3 rounded-lg border border-warning/25 bg-warning/10 p-3">
                  <div className="flex gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
                    <p className="text-xs leading-relaxed text-foreground">
                      Replace this link? Any copy already sent out stops working immediately. NetSuite is
                      updated in the same step — and if it can’t be, nothing changes at all.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="flex-1 gap-1.5" onClick={() => void rotate()} disabled={busy}>
                      {busy && (
                        <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                      )}
                      Replace it
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex-1"
                      onClick={() => setConfirming(false)}
                      disabled={busy}
                    >
                      Keep it
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full gap-1.5"
                  onClick={() => setConfirming(true)}
                  disabled={busy}
                >
                  <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                  Replace link
                </Button>
              )}
            </div>
          )
        )}
      </DialogContent>
    </Dialog>
  );
}
